// @ts-expect-error
import type {DataProxy} from 'react-apollo';

import type {
    QueuedEmail as QueuedEmailFragment,
    EmailQueuedRetryInput,
    RetryQueuedEmail as RetryQueuedEmailMutation,
    DelayQueuedEmail as DelayQueuedEmailMutation,
    DelayQueuedEmailInput,
    EmailQueuedSendNowInput,
    SendNowQueuedEmail as SendNowQueuedEmailMutation,
} from 'nutshell-graphql-types';

export type OptionsObject = {
    onEmailRetried?: (
        proxy: DataProxy,
        update: {
            data?: RetryQueuedEmailMutation;
        }
    ) => void;
    onEmailSendNow?: (
        proxy: DataProxy,
        update: {
            data?: SendNowQueuedEmailMutation;
        }
    ) => void;
    onEmailDelayed?: (
        proxy: DataProxy,
        update: {
            data?: DelayQueuedEmailMutation;
        }
    ) => void;
    onEmailEdit?: () => void;
    onRetry: (arg1: {
        payload: EmailQueuedRetryInput;
        update: (
            proxy: DataProxy,
            update: {
                data?: RetryQueuedEmailMutation;
            }
        ) => void;
    }) => Promise<any>;
    onSendNow: (arg1: {
        payload: EmailQueuedSendNowInput;
        update: (
            proxy: DataProxy,
            update: {
                data?: SendNowQueuedEmailMutation;
            }
        ) => void;
    }) => Promise<any>;
    onDelay: (arg1: {
        payload: DelayQueuedEmailInput;
        update: (
            proxy: DataProxy,
            update: {
                data?: DelayQueuedEmailMutation;
            }
        ) => void;
    }) => Promise<any>;
    onNavToSequenceDetails?: () => void;
    onCancel: () => void;
};

export function getActionMenuOptions(queuedEmail: QueuedEmailFragment, options: OptionsObject) {
    const actions = [];

    const emailSendNow = options.onEmailSendNow;
    if (queuedEmail.isNextScheduledEmail && emailSendNow) {
        actions.push({
            label: 'Send now',
            handleSelect: () => {
                return options.onSendNow({
                    payload: {
                        queuedEmail: {
                            id: queuedEmail.id,
                        },
                    },
                    update: emailSendNow,
                });
            },
        });
    }

    const emailEdit = options.onEmailEdit;
    if (queuedEmail.status === 'SCHEDULED' && emailEdit) {
        actions.push({
            label: 'Edit',
            handleSelect: emailEdit,
        });
    }

    const emailRetry = options.onEmailRetried;
    if (queuedEmail.canRetry && emailRetry) {
        const sequenceInfo = getSequenceInfo(queuedEmail);
        const numAdditionalFollowups =
            sequenceInfo && sequenceInfo.messageNumber
                ? sequenceInfo.messageCount - sequenceInfo.messageNumber
                : 0;
        const retryLabel =
            numAdditionalFollowups > 0
                ? `Retry and reschedule ${numAdditionalFollowups} follow-ups`
                : 'Retry';

        actions.push({
            label: retryLabel,
            handleSelect: () => {
                return options.onRetry({
                    payload: {
                        queuedEmail: {
                            id: queuedEmail.id,
                        },
                    },
                    update: emailRetry,
                });
            },
        });
    }

    const emailDelay = options.onEmailDelayed;
    if (queuedEmail.canDelay && emailDelay) {
        actions.push(
            {
                label: 'Delay 1 business day',
                handleSelect: () => {
                    return options.onDelay({
                        payload: {
                            queuedEmail: {
                                id: queuedEmail.id,
                                delay: {timespan: 1, unit: 'bzd'},
                            },
                        },
                        update: emailDelay,
                    });
                },
            },
            {
                label: 'Delay 1 week',
                handleSelect: () => {
                    return options.onDelay({
                        payload: {
                            queuedEmail: {
                                id: queuedEmail.id,
                                delay: {timespan: 1, unit: 'ww'},
                            },
                        },
                        update: emailDelay,
                    });
                },
            }
        );
    }

    if (options.onNavToSequenceDetails) {
        actions.push({
            label: 'View sequence',
            handleSelect: options.onNavToSequenceDetails,
        });
    }

    if (queuedEmail.status !== 'DELIVERED') {
        actions.push({
            label: 'Cancel',
            variant: 'destructive',
            handleSelect: options.onCancel,
        });
    }

    return actions;
}

export function getSequenceInfo(queuedEmail: QueuedEmailFragment) {
    return queuedEmail.emailSequence && queuedEmail.emailSequence.emailSequenceTemplate
        ? {
              name: queuedEmail.emailSequence.emailSequenceTemplate.name,
              messageNumber: queuedEmail.messageNumber,
              messageCount: queuedEmail.emailSequence.messageCount,
          }
        : null;
}
